import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { toast } from '../components/Toast';

const getStatusBadge = (status) => {
  const styles = {
    scheduled:    { background: "#f0f0f0",  color: "#333"     },
    active:       { background: "#e6f4ea",  color: "#2e7d32"  },
    completed:    { background: "#e8f5e9",  color: "#1b5e20"  },
    discontinued: { background: "#fff3e0",  color: "#e65100"  },
    returning:    { background: "#e3f2fd",  color: "#1565c0"  },
    cancelled:    { background: "#fce4ec",  color: "#b71c1c"  },
  };
  const style = styles[status] || styles.scheduled;
  return (
    <span style={{
      ...style,
      padding: "3px 10px", borderRadius: "12px",
      fontSize: "12px", fontWeight: "600",
      textTransform: "uppercase", display: "inline-block",
    }}>
      {status}
    </span>
  );
};

const formatTime = (dt) => {
  if (!dt) return "-";
  return new Date(dt).toLocaleString('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const getDuration = (start, end) => {
  if (!start || !end) return "-";
  const diff = new Date(end) - new Date(start);
  if (diff <= 0) return "-";
  const hrs  = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

const fmtId = (id) => id ? `TRIP-${String(id).slice(0, 4).toUpperCase()}` : "TRIP----";

const TripReport = () => {
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState(new Set());

    const ITEMS_PER_PAGE = 10;
    const totalPages = Math.ceil(trips.length / ITEMS_PER_PAGE);
    const paginatedTrips = trips.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const handlePrevPage = () => setCurrentPage(p => Math.max(1, p - 1));
    const handleNextPage = () => setCurrentPage(p => Math.min(totalPages, p + 1));

    const toggleRow = (id) => setExpandedRows(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    // Show arrow for trips that have return data OR are completed (went through full return cycle)
    const hasReturn = (t) => t.return_started_at || t.return_ended_at || t.status === 'completed';

    useEffect(() => {
        // Load trips for today on initial load
        const today = new Date().toISOString().split('T')[0];
        setStartDate(today);
        setEndDate(today);
        fetchTrips(today, today);
    }, []);

    const fetchTrips = async (start, end) => {
        setLoading(true);
        try {
            let url = '/trips/history';
            if (start && end) {
                url += `?start_date=${start}&end_date=${end}`;
            }
            const res = await apiFetch(url);
            setTrips(res.data || []);
            setCurrentPage(1); // reset to first page on new results
        } catch (e) {
            toast.error(e.message || "Failed loading trips");
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = () => {
        if (!startDate || !endDate) {
            return toast.error("Please select both start and end dates");
        }
        fetchTrips(startDate, endDate);
    };

    const downloadCSV = () => {
        if (trips.length === 0) return toast.error("No trip data to export");
        
        const headers = ['"Trip ID","Vehicle","Driver","Route","Status","Start Time","End Time","Duration","Return Start","Return End","Return Duration"'];
        const rows = trips.map(t => {
            const forwardEndTime = t.forward_ended_at || t.ended_at;
            const duration = getDuration(t.started_at, forwardEndTime);
            const returnStart = t.return_started_at || t.forward_ended_at;
            const returnEnd   = t.return_ended_at || (t.status === 'completed' ? (t.completed_at || t.ended_at) : null);
            const returnDuration = getDuration(returnStart, returnEnd);
            return `"${fmtId(t.trip_id)}","${t.vehicle_code || '-'}","${t.driver_name || '-'}","${t.route_name || '-'}","${t.status}","${formatTime(t.started_at)}","${formatTime(forwardEndTime)}","${duration}","${formatTime(returnStart)}","${formatTime(returnEnd)}","${returnDuration}"`;
        });
        const csvContent = headers.concat(rows).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); 
        a.href = url;
        const start = startDate || 'all';
        const end = endDate || 'all';
        a.download = `trips_report_${start}_to_${end}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div>
            <h1 className="page-title text-gradient" style={{ margin: 0, marginBottom: '24px' }}>Trip Report</h1>

            <div style={{ 
                display: 'flex', 
                gap: '10px', 
                alignItems: 'center', 
                marginBottom: '16px', 
                flexWrap: 'wrap' 
            }}>
                <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    style={{ 
                        padding: '6px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '6px', 
                        height: '40px' 
                    }}
                />
                <span>to</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    style={{ 
                        padding: '6px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '6px', 
                        height: '40px' 
                    }}
                />
                <button
                    onClick={handleGenerate}
                    style={{
                        padding: '6px 16px',
                        background: '#fff',
                        color: '#000',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        height: '40px'
                    }}
                >
                    Generate Report
                </button>
                <button
                    onClick={downloadCSV}
                    style={{
                        padding: '6px 16px',
                        background: '#000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        height: '40px',
                        marginLeft: 'auto',
                    }}
                >
                    ⬇ Download CSV
                </button>
            </div>

            <div className="glass-panel" style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ fontSize: '14px', width: '100%' }}>
                    <thead>
                        <tr style={{ height: '48px' }}>
                            <th style={{ padding: '10px 8px', width: '32px' }}></th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Trip ID</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Vehicle</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Driver Name</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Route</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Status</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Start Time</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>End Time</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr style={{ height: '48px' }}>
                                <td colSpan="9" style={{ textAlign: 'center', padding: '10px 16px' }}>Loading trips...</td>
                            </tr>
                        ) : trips.length === 0 ? (
                            <tr style={{ height: '48px' }}>
                                <td colSpan="9" style={{ textAlign: 'center', padding: '10px 16px' }}>No trips found. Select date range and click Generate Report.</td>
                            </tr>
                        ) : (
                            paginatedTrips.map((t) => {
                                const isExpanded = expandedRows.has(t.history_id);
                                const showArrow = hasReturn(t);
                                // End Time = forward trip end only (not return/park time)
                                const forwardEndTime = t.forward_ended_at || t.ended_at;
                                const duration = getDuration(t.started_at, forwardEndTime);
                                // Return leg times — fall back for old trips without dedicated columns
                                // return_started_at ≈ forward_ended_at (when forward leg ended, return began)
                                // return_ended_at   ≈ completed_at / ended_at (when trip was fully parked)
                                const returnStart = t.return_started_at || t.forward_ended_at;
                                const returnEnd   = t.return_ended_at || (t.status === 'completed' ? (t.completed_at || t.ended_at) : null);
                                const returnDuration = getDuration(returnStart, returnEnd);
                                return (
                                    <React.Fragment key={t.history_id || t.trip_id}>
                                        {/* Main trip row */}
                                        <tr style={{ height: '48px', background: isExpanded ? '#fdf8f3' : undefined }}>
                                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                                {showArrow ? (
                                                    <button
                                                        onClick={() => toggleRow(t.history_id)}
                                                        style={{
                                                            background: 'none', border: 'none',
                                                            cursor: 'pointer', fontSize: '14px',
                                                            color: '#8B5E1A', padding: '2px 4px',
                                                            transition: 'transform 0.2s',
                                                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                            display: 'inline-block',
                                                        }}
                                                        title="Show return leg"
                                                    >
                                                        ▶
                                                    </button>
                                                ) : null}
                                            </td>
                                            <td style={{ padding: '10px 16px', fontFamily: 'monospace' }}>{fmtId(t.trip_id)}</td>
                                            <td style={{ padding: '10px 16px' }}>{t.vehicle_code || '-'}</td>
                                            <td style={{ padding: '10px 16px' }}>{t.driver_name || '-'}</td>
                                            <td style={{ padding: '10px 16px' }}>{t.route_name || '-'}</td>
                                            <td style={{ padding: '10px 16px' }}>{getStatusBadge(t.status)}</td>
                                            <td style={{ padding: '10px 16px' }}>{formatTime(t.started_at)}</td>
                                            <td style={{ padding: '10px 16px' }}>{formatTime(forwardEndTime)}</td>
                                            <td style={{ padding: '10px 16px' }}>{duration}</td>
                                        </tr>

                                        {/* Return leg sub-row — values aligned under matching columns */}
                                        {isExpanded && showArrow && (
                                            <tr style={{ background: '#fdf3e3', borderBottom: '1px solid #f0e0c0' }}>
                                                {/* empty arrow cell */}
                                                <td></td>
                                                {/* Trip ID — empty */}
                                                <td></td>
                                                {/* Vehicle — empty */}
                                                <td></td>
                                                {/* Driver — empty */}
                                                <td></td>
                                                {/* Route — empty */}
                                                <td></td>
                                                {/* Status column → Return Leg badge */}
                                                <td style={{ padding: '8px 16px' }}>
                                                    <span style={{
                                                        background: '#e3f2fd', color: '#1565c0',
                                                        padding: '2px 10px', borderRadius: '10px',
                                                        fontWeight: 600, fontSize: '11px',
                                                        textTransform: 'uppercase',
                                                    }}>
                                                        ↩ Return
                                                    </span>
                                                </td>
                                                {/* Start Time column → Return Start */}
                                                <td style={{ padding: '8px 16px', fontSize: '13px', color: '#5a3e00' }}>
                                                    {formatTime(returnStart)}
                                                </td>
                                                {/* End Time column → Return End */}
                                                <td style={{ padding: '8px 16px', fontSize: '13px', color: '#5a3e00' }}>
                                                    {formatTime(returnEnd)}
                                                </td>
                                                {/* Duration column → Return Duration */}
                                                <td style={{ padding: '8px 16px', fontSize: '13px', color: '#5a3e00' }}>
                                                    {returnDuration}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {trips.length > ITEMS_PER_PAGE && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '16px',
                        borderTop: '1px solid var(--border-color)',
                    }}>
                        <button
                            className="btn-secondary"
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                            style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
                        >
                            ← Previous
                        </button>
                        <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                            Page {currentPage} of {totalPages}
                            <span style={{ marginLeft: '8px', color: 'var(--text-muted)', fontSize: '12px' }}>
                                ({trips.length} total)
                            </span>
                        </span>
                        <button
                            className="btn-secondary"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TripReport;
